import * as React from "react";
import { Tray } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { TimelineRow } from "../TimelineRow";
import type { CustomerDetailLabels, CustomerTimelineEvent, CustomerTimelineEventKind } from "../types";
import { formatDateShort, groupTimelineByDay, relativeDaysFromNow } from "../utils";

interface Props {
  events: CustomerTimelineEvent[];
  labels: CustomerDetailLabels;
}

type FilterKey = "all" | CustomerTimelineEventKind;

export function TimelineTab({ events, labels }: Props) {
  const [active, setActive] = React.useState<Set<FilterKey>>(new Set(["all"]));

  const filters: { key: FilterKey; label: string }[] = [
    { key: "all", label: labels.timeline.filters.all },
    { key: "appointment", label: labels.timeline.filters.appointment },
    { key: "payment", label: labels.timeline.filters.payment },
    { key: "vaccination", label: labels.timeline.filters.vaccination },
    { key: "note", label: labels.timeline.filters.note },
  ];

  const filtered = React.useMemo(() => {
    if (active.has("all") || active.size === 0) return events;
    return events.filter((e) => active.has(e.kind));
  }, [events, active]);

  const groups = React.useMemo(() => groupTimelineByDay(filtered), [filtered]);

  const toggle = (k: FilterKey) => {
    setActive((prev) => {
      const next = new Set(prev);
      if (k === "all") return new Set<FilterKey>(["all"]);
      next.delete("all");
      if (next.has(k)) next.delete(k);
      else next.add(k);
      if (next.size === 0) next.add("all");
      return next;
    });
  };

  if (events.length === 0) {
    return (
      <div className="rounded-biz-card border border-dashed border-biz-border bg-biz-surface p-10 text-center">
        <div className="mx-auto h-14 w-14 rounded-full bg-biz-muted flex items-center justify-center text-biz-foreground-muted">
          <Tray size={24} weight="regular" />
        </div>
        <p className="mt-3 text-sm text-biz-foreground-muted">{labels.timeline.empty}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-5" role="group" aria-label={labels.timeline.title}>
        {filters.map((f) => {
          const isActive = active.has(f.key);
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => toggle(f.key)}
              aria-pressed={isActive}
              className={cn(
                "h-10 px-3.5 rounded-full text-sm font-semibold biz-focus-ring transition-colors border",
                isActive
                  ? "bg-biz-primary text-biz-primary-foreground border-biz-primary"
                  : "bg-biz-surface text-biz-foreground-muted border-biz-border hover:bg-biz-muted",
              )}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <div className="space-y-6">
        {groups.map(({ dayKey, date, items }) => (
          <section key={dayKey}>
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-sm font-display font-bold text-biz-foreground">
                {dayLabel(date, labels)}
              </h3>
              <span className="text-xs text-biz-foreground-subtle font-mono-num">
                {formatDateShort(date.toISOString())}
              </span>
            </div>
            <ul className="rounded-biz-card border border-biz-border bg-biz-surface p-4 shadow-biz-sm">
              {items.map((e, i) => (
                <TimelineRow key={e.id} event={e} labels={labels} index={i} />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

function dayLabel(date: Date, labels: CustomerDetailLabels): string {
  const diff = relativeDaysFromNow(date.toISOString());
  if (diff === 0) return labels.timeline.today;
  if (diff === 1) return labels.timeline.yesterday;
  if (diff > 1) return labels.timeline.relativeDays(diff);
  return formatDateShort(date.toISOString());
}
